export interface AgentFlag {
  id: string;
  flag: string;
  label: string;
  description: string;
}

export interface AgentCliConfig {
  command: string;
  displayName: string;
  flags: AgentFlag[];
  /**
   * Flag ids pre-selected by default in the project wizard. These are the
   * user's typical run settings; they persist as the project's defaults, but
   * the very FIRST launch still suppresses resume-family flags (see
   * {@link FIRST_LAUNCH_SUPPRESSED}) so a brand-new session starts fresh.
   */
  defaultFlags?: string[];
  /** Regex pattern that signals agent completion (matched in Rust against raw output). */
  completionPattern?: string;
  /**
   * If set, the launch-options panel renders a model dropdown that picks a
   * value to append after this flag. The dropdown's options come from the
   * Rust `list_agent_models(agentType)` command, cached for 24h in
   * `useAgentModelsStore`. If `list_agent_models` returns an empty list for
   * this agent, the dropdown silently disappears and the user can still type
   * `<flag> <value>` into customFlags by hand.
   */
  modelFlag?: string;
  /**
   * CLI flag this agent uses to APPEND to its system prompt. When set,
   * {@link buildAgentCommand} injects the AtelierCode work-queue contract
   * (`.ateliercode/work/WORKFLOW.md`) so the launched agent is automatically
   * workflow-aware (see WORKFLOW.md). The contract is read at shell-eval time
   * from the `ATELIER_WORKFLOW` env var the core sets on the terminal — passing
   * the *path* (not multi-KB inline text) keeps the typed PTY command short and
   * robust to quoting/length limits, and it no-ops gracefully when the file is
   * absent (the env var is unset → empty append).
   */
  systemPromptAppendFlag?: string;
}

export const AGENT_CLI_CONFIG: Record<string, AgentCliConfig> = {
  claude: {
    command: 'claude',
    displayName: 'Claude Code',
    completionPattern: '\\$[\\d.]+\\s+\\|.*tokens',
    systemPromptAppendFlag: '--append-system-prompt',
    // Continue + Skip Permissions on by default (the common way to run Claude
    // Code here). First launch still starts fresh — see buildAgentCommand.
    defaultFlags: ['skip-permissions', 'continue'],
    flags: [
      {
        id: 'skip-permissions',
        flag: '--dangerously-skip-permissions',
        label: 'Skip Permissions',
        description: 'Auto-approve all tool use without prompting',
      },
      {
        id: 'continue',
        flag: '--continue',
        label: 'Continue',
        description: 'Resume the most recent conversation',
      },
      {
        id: 'resume',
        flag: '--resume',
        label: 'Resume (picker)',
        description: 'Open the session picker to choose which conversation to resume',
      },
    ],
  },
  copilot: {
    command: 'copilot',
    displayName: 'GitHub Copilot CLI',
    // Copilot's TUI has no stable single-line "done" marker (like OpenCode), so
    // completion detection falls back to the daemon's silence heuristic.
    // Model is picked in-session via /model (no reliable --model flag to model
    // here); custom system-prompt injection uses .github/copilot-instructions.md,
    // not a CLI flag, so no systemPromptAppendFlag.
    // Allow-all-tools on by default to match the hands-off way agents run here;
    // deny rules (e.g. --deny-tool 'shell(rm)') still win and can go in customFlags.
    defaultFlags: ['allow-all-tools'],
    flags: [
      {
        id: 'allow-all-tools',
        flag: '--allow-all-tools',
        label: 'Allow All Tools',
        description: 'Auto-approve all tool use without prompting (deny rules still take precedence)',
      },
      {
        id: 'continue',
        flag: '--continue',
        label: 'Continue',
        description: 'Resume the most recent Copilot session in this directory',
      },
      {
        id: 'resume',
        flag: '--resume',
        label: 'Resume (picker)',
        description: 'Browse recent Copilot sessions and pick one to resume',
      },
    ],
  },
  gemini: {
    command: 'gemini',
    displayName: 'Gemini CLI',
    completionPattern: 'Total tokens:',
    flags: [
      {
        id: 'resume',
        flag: '--resume',
        label: 'Resume Last Session',
        description: 'Pick up the most recent Gemini chat session where it left off',
      },
      {
        id: 'approval-auto-edit',
        flag: '--approval-mode auto_edit',
        label: 'Auto-approve Edits',
        description: 'Auto-approve edit/write tool calls without prompting (Gemini CLI 0.36+)',
      },
      {
        id: 'yolo',
        flag: '-y',
        label: 'Yolo Mode',
        description: 'Auto-approve all actions without prompting (deprecated alias for full --approval-mode yolo)',
      },
      {
        id: 'sandbox',
        flag: '-s',
        label: 'Sandbox',
        description: 'Run tools inside the configured sandbox (Docker / Podman / etc.)',
      },
    ],
  },
  codex: {
    command: 'codex',
    displayName: 'Codex CLI',
    flags: [
      {
        id: 'auto-approve',
        flag: '--approval-policy never',
        label: 'Auto-approve actions',
        description: 'Skip all approval prompts (equivalent to --approval-policy never)',
      },
      {
        id: 'on-failure',
        flag: '--approval-policy on-failure',
        label: 'Ask only on failure',
        description: 'Only prompt for approval when a tool call fails',
      },
    ],
  },
  opencode: {
    command: 'opencode',
    displayName: 'OpenCode',
    modelFlag: '--model',
    // Completion pattern intentionally omitted — OpenCode's TUI doesn't have
    // a stable single-line "done" marker the way Claude / Gemini do. The
    // daemon's data-event heuristic (15s+ of sustained output followed by
    // 10s of silence) handles completion detection well enough until
    // upstream surfaces a marker.
    //
    // Flag set mirrors the boolean flags from `opencode --help` (top-level
    // / TUI mode, which is what AtelierCode launches). Value-bearing
    // options like --model <provider/id>, --agent <name>, --session <id>,
    // --log-level <level> are best passed through the free-form
    // customFlags box rather than modeled as static toggles.
    flags: [
      {
        id: 'continue',
        flag: '--continue',
        label: 'Continue',
        description: 'Resume the most recent OpenCode session in this directory',
      },
      {
        id: 'fork',
        flag: '--fork',
        label: 'Fork on resume',
        description: 'Fork the session when continuing — keeps the original chain intact (requires Continue)',
      },
      {
        id: 'pure',
        flag: '--pure',
        label: 'Pure (no plugins)',
        description: 'Run without external OpenCode plugins — useful for reproducing bugs',
      },
      {
        id: 'print-logs',
        flag: '--print-logs',
        label: 'Print logs to stderr',
        description: 'Stream OpenCode internal logs to the terminal — diagnostic only',
      },
    ],
  },
  aider: {
    command: 'aider',
    displayName: 'Aider',
    completionPattern: 'Tokens:.*sent',
    flags: [
      {
        id: 'yes-always',
        flag: '--yes-always',
        label: 'Yes Always',
        description: 'Always say yes to prompts',
      },
      {
        id: 'auto-commits',
        flag: '--auto-commits',
        label: 'Auto Commits',
        description: 'Automatically commit changes',
      },
    ],
  },
};

/**
 * Build the full launch command string for an agent.
 *
 * `enabledFlags` are looked up against the agent's defined flag list and
 * appended in declaration order. `model`, when set, is appended as
 * `<config.modelFlag> <model>` (only meaningful for agents whose config
 * declares a `modelFlag`). `customFlags` is a free-form string the user
 * can put in the project settings — it gets appended verbatim after the
 * declared flags so users can pass arbitrary launch options the UI doesn't
 * model yet (e.g. `--variant high --cd /tmp/foo`).
 */
/** Flag ids suppressed on a project's FIRST agent launch — there's no prior
 *  conversation to resume/continue/fork yet, so a fresh session is started even
 *  though these stay enabled as the persisted defaults for later launches. */
export const FIRST_LAUNCH_SUPPRESSED = new Set(['continue', 'resume', 'fork']);

/** The flag ids the wizard pre-selects for an agent (empty for unknown/agents
 *  with no defaults). */
export function defaultFlagsFor(agentType: string): string[] {
  return AGENT_CLI_CONFIG[agentType]?.defaultFlags ?? [];
}

export function buildAgentCommand(
  agentType: string,
  enabledFlags: string[],
  customFlags?: string,
  model?: string | null,
  opts?: { isFirstLaunch?: boolean; shell?: string },
): string | null {
  const config = AGENT_CLI_CONFIG[agentType];
  if (!config) return null;
  const parts = [config.command];
  for (const flag of config.flags) {
    if (enabledFlags.includes(flag.id)) {
      // Fresh first run: keep the flag as a saved default but don't apply it now.
      if (opts?.isFirstLaunch && FIRST_LAUNCH_SUPPRESSED.has(flag.id)) continue;
      parts.push(flag.flag);
    }
  }
  if (config.modelFlag && model && model.trim().length > 0) {
    parts.push(config.modelFlag, model.trim());
  }
  // Make the launched agent automatically workflow-aware: if the agent has a
  // system-prompt-append flag, feed it the AtelierCode work-queue contract, read
  // at shell-eval time from the ATELIER_WORKFLOW env var the core sets on the
  // terminal (pointing at .ateliercode/work/WORKFLOW.md when present). The
  // read is SHELL-SPECIFIC — the old bash form (`$(cat "$ATELIER_WORKFLOW"
  // 2>/dev/null)`) is invalid in PowerShell (needs $env: + different redirect),
  // which on Windows fed the agent a broken --append-system-prompt. Emit the
  // right substitution per shell; degrade to empty when the var/file is absent.
  let pwshWorkflowSplat = '';
  if (config.systemPromptAppendFlag) {
    const shell = (opts?.shell ?? '').toLowerCase();
    // `shell` is a stored PREFERENCE, not the shell that necessarily runs. Windows
    // PowerShell / pwsh / cmd only apply on Windows — on Linux/macOS the daemon
    // falls back to bash (resolve_shell), so emitting a PowerShell/cmd form there
    // yields "bash: syntax error near unexpected token '('". Gate those forms to
    // Windows; every non-Windows platform uses the POSIX (bash-family) form.
    const isWindows =
      typeof navigator !== 'undefined' && /win/i.test(navigator.platform);
    if (isWindows && (shell === 'pwsh' || shell === 'powershell')) {
      // Windows PowerShell 5.1 DROPS an empty-string argument to a native command,
      // so the old `--append-system-prompt "$(...)"` form made claude see the flag
      // with NO value ("error: option '--append-system-prompt <prompt>' argument
      // missing") whenever no workflow file was present. Build the flag+value into
      // a splat array that is EMPTY when there's no workflow (nothing is passed)
      // and holds [flag, content] when there is (content passed as ONE argument,
      // spaces/newlines safe). Setup is prepended and `@__acwf` splatted below.
      pwshWorkflowSplat =
        '$__acwf=@(); if($env:ATELIER_WORKFLOW -and (Test-Path $env:ATELIER_WORKFLOW))' +
        `{$__acwf=@('${config.systemPromptAppendFlag}',(Get-Content -Raw $env:ATELIER_WORKFLOW))}; `;
    } else if (isWindows && shell === 'cmd') {
      // cmd.exe has no inline command substitution — skip the workflow append
      // rather than emit something broken. (Agent shells are pwsh/bash in practice.)
    } else {
      // bash-family (bash/zsh/sh/gitbash/wsl): the original POSIX form. An unset
      // var / missing file yields an empty "" arg, which bash passes through fine.
      parts.push(
        config.systemPromptAppendFlag,
        '"$(cat "$ATELIER_WORKFLOW" 2>/dev/null)"',
      );
    }
  }
  const trimmedCustom = customFlags?.trim();
  if (trimmedCustom) {
    parts.push(trimmedCustom);
  }
  const cmd = parts.join(' ');
  // PowerShell: prepend the workflow-splat setup and splat it onto the command so
  // the append flag is present only when a workflow file exists.
  return pwshWorkflowSplat ? `${pwshWorkflowSplat}${cmd} @__acwf` : cmd;
}
